import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { shallowEqual } from "react-redux";
import { getPageModules } from "../../selectors/modules.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  Language,
  Modules,
  QuestionAndAnswerModuleSettings,
  QuestionsAndAnswersModuleSettings,
  TranslatedModule,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getTranslatedModulesByParentId,
} from "../../utils/utils.js";
import JsonLd from "../JsonLd.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import QuestionAndAnswerModule from "./QuestionAndAnswerModule.js";

type Props = BaseModuleProps<QuestionsAndAnswersModuleSettings>;

const QuestionsAndAnswersModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    settings: { textAlign },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
  activeModuleId,
  isPreview,
  pageId,
  queries,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const modules = useAppSelector((state) => state.modules);
  const richEditor = useAppSelector((state) => state.richEditor);
  const moduleIds = useAppSelector(
    (state) =>
      getPageModules(state.modules, pageId)
        .filter(({ type }) => type === "QuestionsAndAnswersModule")
        .map(({ id }) => id),
    shallowEqual,
  );
  const isLastQAModule = moduleId === moduleIds.slice(-1)[0];
  const submodules = getQASubmodules({ modules, languageId, pageId, moduleId });
  const metadata = isLastQAModule
    ? getMetadata(
        getAllPageQASubmodules({ modules, languageId, moduleIds, pageId }),
      )
    : undefined;

  return (
    <ModuleWithHeadings
      id={moduleId}
      colors={{ background: scheme.main.separator, color: scheme.main.text }}
      title={title}
      subtitle={subtitle}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <dl>
          {submodules.map((submodule) => (
            <QuestionAndAnswerModule
              key={submodule.id}
              activeModuleId={activeModuleId}
              isActive={activeModuleId === submodule.id}
              isFirstOnPage={isFirstOnPage}
              isPreview={isPreview}
              pageId={pageId}
              queries={queries}
              translatedModule={submodule}
              scheme={scheme}
              richEditor={richEditor}
            />
          ))}
        </dl>
        {metadata && <JsonLd data={metadata} />}
      </div>
    </ModuleWithHeadings>
  );
};

interface MetaQuestion {
  "@type": "Question";
  name: string;
  acceptedAnswer: { "@type": "Answer"; text: string };
}

interface Metadata {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: MetaQuestion[];
}

/**
 * Get the JSON-LD metadata for all questions and answers on the current page.
 */
const getMetadata = (
  submodules: TranslatedModule<QuestionAndAnswerModuleSettings>[],
): Metadata => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: submodules.map(
    ({
      translation: {
        settings: { title, plainDescription },
      },
    }) => ({
      "@type": "Question",
      name: title,
      acceptedAnswer: { "@type": "Answer", text: plainDescription ?? "" },
    }),
  ),
});

const getQASubmodules = ({
  modules,
  languageId,
  pageId,
  moduleId,
}: {
  modules: Modules;
  languageId: Language;
  pageId: string;
  moduleId: string;
}) =>
  getTranslatedModulesByParentId<QuestionAndAnswerModuleSettings>({
    modules,
    languageId,
    pageId,
    parentId: moduleId,
    moduleType: "QuestionAndAnswerModule",
  });

/**
 * Get all QuestionAndAnswerModules of the current page
 * (children of all QuestionsAndAnswersModules)
 */
const getAllPageQASubmodules = ({
  modules,
  languageId,
  pageId,
  moduleIds,
}: {
  modules: Modules;
  languageId: Language;
  pageId: string;
  moduleIds: string[];
}) =>
  moduleIds.reduce<TranslatedModule<QuestionAndAnswerModuleSettings>[]>(
    (carry, moduleId) => [
      ...carry,
      ...getQASubmodules({ modules, languageId, pageId, moduleId }),
    ],
    [],
  );

export default QuestionsAndAnswersModule;
